import type { PreSessionReportPreview } from '@signalvote/shared';
import { formatDate } from '../utils';

/**
 * The pre-session report modal's CTA state machine (GitHub #164, ADR-039),
 * extracted as a pure function so its many gate branches are unit-testable
 * without rendering. The modal reads the preview off
 * `GET /obligations/{id}/pre-session-preview`; `generatedAt` comes off the
 * obligation (`GET /obligations`), which is why it isn't on the preview DTO.
 *
 * Precedence (first match wins):
 *   1. generatedAt   — the file is out; the deck card, not the modal, owns
 *      "Mark as reported" (this branch only fires if the modal is somehow open
 *      post-generate; the card gates the modal to the pre-generate state).
 *   2. need-date     — the session date must be confirmed before anything else.
 *   3. window-open   — the disclosure window hasn't closed; name when it does.
 *   4. blocked       — a treasurer-fixable gift blocks the file.
 *   5. no-donors     — nobody crossed $1,000 in the window; nothing to file.
 *   6. ready         — enabled follows the server `canGenerate` flag so a stale
 *      client can never produce a hollow or premature file.
 *
 * Generate is gated on `sessionDateConfirmed && windowClosed && canGenerate` —
 * expressed here as the composite `enabled` of the `ready` branch.
 */

export type PreSessionCtaKind =
  | 'reported'
  | 'need-date'
  | 'window-open'
  | 'blocked'
  | 'no-donors'
  | 'ready';

export interface PreSessionCta {
  kind: PreSessionCtaKind;
  label: string;
  enabled: boolean;
}

/** The preview fields the CTA reads, plus `generatedAt` (off the obligation). */
export type PreSessionCtaInput = Pick<
  PreSessionReportPreview,
  | 'sessionDateConfirmed'
  | 'windowClosed'
  | 'windowEnd'
  | 'blockingGaps'
  | 'qualifyingDonorCount'
  | 'canGenerate'
> & { generatedAt: string | null };

export function deriveCta(input: PreSessionCtaInput): PreSessionCta {
  if (input.generatedAt) {
    return { kind: 'reported', label: 'Mark as reported', enabled: true };
  }
  if (!input.sessionDateConfirmed) {
    return { kind: 'need-date', label: 'Confirm the session date first', enabled: false };
  }
  if (!input.windowClosed) {
    return {
      kind: 'window-open',
      label: `You can generate after ${formatDate(input.windowEnd)}`,
      enabled: false,
    };
  }
  if (input.blockingGaps.length > 0) {
    return { kind: 'blocked', label: 'Generate report', enabled: false };
  }
  if (input.qualifyingDonorCount === 0) {
    return { kind: 'no-donors', label: 'Nothing to file', enabled: false };
  }
  return { kind: 'ready', label: 'Generate report', enabled: input.canGenerate };
}
